/**
 * @fileoverview Event-driven animation hooks: selection feedback on options
 * items and TapTargetStyle enter/idle/tap animations on buttons.
 * @module screen-view-v2/animations/useEventAnimation
 *
 * CSS animations never restart when an identical class is re-added, so both
 * hooks restart via a two-commit "reset → play" cycle: the triggering commit
 * renders WITHOUT the animation class, an immediate follow-up effect renders
 * WITH it — a fresh animation every time.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { TapTargetStyle } from '@segmently/schema-models/v2';
import { useScreenAnimationContext } from './AnimationContext';
import {
  buildElementAnimationSpec,
  type ResolvedElementAnimationSpec,
} from './elementAnimationSpec';
import {
  OPTIONS_ITEM_SELECTION_REF,
  OPTIONS_ITEM_DESELECTION_REF,
  PAYWALL_PRODUCT_ITEM_SELECTION_REF,
  PAYWALL_PRODUCT_ITEM_DESELECTION_REF,
} from './resolveScreenAnimationPlan';
import type { ElementAnimationProps } from './useElementAnimation';
import { ssrEntranceProps } from './ssrEntrance';

const EMPTY: ElementAnimationProps = {};

/**
 * Selection/deselection plan refs for the item family the editPath belongs
 * to. Options items stay the default (backward compatible); paywall product
 * offers get their own `#` refs.
 */
function selectionRefsForEditPath(editPath: string | undefined): {
  selection: string;
  deselection: string;
} {
  const ref = editPath?.startsWith('content.') ? editPath.slice('content.'.length) : editPath;
  if (ref && /^paywall\.products\.items\.\d+$/.test(ref)) {
    return {
      selection: PAYWALL_PRODUCT_ITEM_SELECTION_REF,
      deselection: PAYWALL_PRODUCT_ITEM_DESELECTION_REF,
    };
  }
  return { selection: OPTIONS_ITEM_SELECTION_REF, deselection: OPTIONS_ITEM_DESELECTION_REF };
}

// ============================================================================
// Selection feedback (OptionsList.selectionAnimation / deselectionAnimation)
// ============================================================================

interface EventAnimState {
  spec: ResolvedElementAnimationSpec | null;
  /** Reset commit: render without the class so the next commit restarts it */
  resetting: boolean;
}

/**
 * Plays the list-level selection/deselection animation on an options item
 * whenever its `selected` prop flips. Initial mount never animates.
 */
export function useSelectionAnimation(
  editPath: string | undefined,
  selected: boolean,
): ElementAnimationProps {
  const ctx = useScreenAnimationContext();
  const [state, setState] = useState<EventAnimState>({ spec: null, resetting: false });
  const prevSelectedRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!ctx?.eventsEnabled || !editPath) {
      prevSelectedRef.current = selected;
      return;
    }
    if (prevSelectedRef.current === null || prevSelectedRef.current === selected) {
      prevSelectedRef.current = selected;
      return;
    }
    prevSelectedRef.current = selected;

    const refs = selectionRefsForEditPath(editPath);
    const entry = ctx.plan.get(selected ? refs.selection : refs.deselection);
    if (!entry) {
      setState({ spec: null, resetting: false });
      return;
    }
    const spec = buildElementAnimationSpec(entry.animation, {
      reducedMotion: ctx.reducedMotion,
      defaultTrigger: entry.defaultTrigger ?? 'onSelectionChange',
    });
    setState({ spec, resetting: spec != null });
  }, [selected, ctx, editPath]);

  useEffect(() => {
    if (state.resetting) {
      setState((s) => ({ ...s, resetting: false }));
    }
  }, [state.resetting]);

  return useMemo<ElementAnimationProps>(() => {
    if (!state.spec || state.resetting || !ctx?.eventsEnabled) return EMPTY;
    return {
      className: state.spec.className,
      style: state.spec.vars as CSSProperties,
    };
  }, [state, ctx?.eventsEnabled]);
}

// ============================================================================
// TapTargetStyle enter / idle / tap (Button)
// ============================================================================

type TapTargetPhase = 'enter' | 'idle' | 'tap' | 'tap-reset' | 'done';

export interface TapTargetAnimationResult {
  animProps: ElementAnimationProps;
  /** Call from the click handler — plays tapAnimation without blocking the action */
  notifyTap: () => void;
  /**
   * True when any of the NEW fields (enterAnimation/idleAnimation/tapAnimation)
   * produced a spec — the caller must then skip the legacy AnimationConfig path.
   */
  hasNewAnimations: boolean;
}

const PHASE_END_MARGIN_MS = 50;

/**
 * Drives TapTargetStyle.enterAnimation → idleAnimation → (tapAnimation on
 * click, then back to idle). New fields take priority over the legacy
 * `appearance.animation` (AnimationConfig) — the caller keeps the legacy path
 * as fallback when no new field is present.
 */
export function useTapTargetAnimation(
  appearance: TapTargetStyle | null | undefined,
): TapTargetAnimationResult {
  const ctx = useScreenAnimationContext();
  const enabled = ctx?.enabled ?? false;
  const eventsEnabled = ctx?.eventsEnabled ?? false;
  const reducedMotion = ctx?.reducedMotion ?? false;
  // Mirrors useElementAnimation's pending-hold: a button entrance must not
  // play (nor flash the final state) while the screen transition is still
  // running. Default true — only the live web-shell threads a false.
  const screenEntered = ctx?.screenEntered ?? true;
  // The static page already painted this button: no ENTRANCE-category enter
  // phase on the client takeover (AMV-AC-036) — an emphasis enter, idle and
  // tap stay. The enter spec still counts for `hasNewAnimations`, so the
  // legacy AnimationConfig path stays off.
  const skipEnter = ctx?.skipMountEntrances ?? false;
  const ssrEntranceOnly = ctx?.ssrEntranceOnly ?? false;

  // enter/idle belong to the entrance/play window (`enabled`); tap is
  // user-initiated feedback and stays previewable whenever events are
  // (`eventsEnabled`) — clicking a button in the editor must always respond.
  const specs = useMemo(() => {
    return {
      enter: enabled
        ? buildElementAnimationSpec(appearance?.enterAnimation, {
          reducedMotion, defaultTrigger: 'onAppear',
        })
        : null,
      idle: enabled
        ? buildElementAnimationSpec(appearance?.idleAnimation, {
          reducedMotion, defaultTrigger: 'onAppear', forceLoop: true,
        })
        : null,
      tap: eventsEnabled
        ? buildElementAnimationSpec(appearance?.tapAnimation, {
          reducedMotion, defaultTrigger: 'onTap',
        })
        : null,
    };
  }, [appearance, enabled, eventsEnabled, reducedMotion]);

  const hasNewAnimations = Boolean(specs.enter || specs.idle || specs.tap);

  const [phase, setPhase] = useState<TapTargetPhase>('done');

  // (Re)initialize the phase machine whenever the specs change
  useEffect(() => {
    if (!hasNewAnimations) {
      setPhase('done');
      return;
    }
    const enterSkipped = skipEnter && specs.enter?.category === 'entrance';
    setPhase(specs.enter && !enterSkipped ? 'enter' : specs.idle ? 'idle' : 'done');
  }, [specs, hasNewAnimations, skipEnter]);

  // enter → idle handoff after the enter animation finishes. While the
  // screen transition is still running the enter phase is held PAUSED
  // (see animProps below) — the handoff timer must not tick until settle,
  // or idle would take over while the button is still hidden mid-slide.
  useEffect(() => {
    if (phase !== 'enter' || !specs.enter || !screenEntered) return;
    const timer = setTimeout(
      () => setPhase(specs.idle ? 'idle' : 'done'),
      specs.enter.delayMs + specs.enter.durationMs + PHASE_END_MARGIN_MS,
    );
    return () => clearTimeout(timer);
  }, [phase, specs, screenEntered]);

  // tap-reset (classless commit) → tap
  useEffect(() => {
    if (phase === 'tap-reset') setPhase('tap');
  }, [phase]);

  // tap → back to idle after it finishes
  useEffect(() => {
    if (phase !== 'tap' || !specs.tap) return;
    const timer = setTimeout(
      () => setPhase(specs.idle ? 'idle' : 'done'),
      specs.tap.delayMs + specs.tap.durationMs + PHASE_END_MARGIN_MS,
    );
    return () => clearTimeout(timer);
  }, [phase, specs]);

  const notifyTap = useMemo(() => () => {
    if (!specs.tap) return;
    setPhase('tap-reset');
  }, [specs.tap]);

  const animProps = useMemo<ElementAnimationProps>(() => {
    // FSE: the server render has no phase machine (effects never run) — it paints the ENTRANCE-category enter phase
    // running; idle loops and tap feedback start on the client.
    if (ssrEntranceOnly) {
      return specs.enter?.category === 'entrance' ? ssrEntranceProps(specs.enter) : EMPTY;
    }
    const active = phase === 'enter' ? specs.enter
      : phase === 'idle' ? specs.idle
        : phase === 'tap' ? specs.tap
          : null;
    if (!active) return EMPTY;
    // Pending-hold: keep the enter animation paused at its `from` state
    // (backwards fill) until the screen transition settles — never defer the
    // phase init without the paused class, or the button would flash its
    // final state and double-land like the pre-fix element entrances.
    const style = phase === 'enter' && !screenEntered
      ? { ...active.vars, '--sv2-anim-play': 'paused' }
      : active.vars;
    return { className: active.className, style: style as CSSProperties };
  }, [phase, specs, screenEntered, ssrEntranceOnly]);

  return { animProps, notifyTap, hasNewAnimations };
}
